import { applyDocumentChatConversationToOpenTabs } from 'features/embeddedSidebar/chatHistory/agenticChatHistoryActions'
import {
  type RecordedAgenticChatSession,
  useAgenticChatSessionHistory,
} from 'features/embeddedSidebar/hooks/useAgenticChatSessionHistory'
import { useDocumentChatSessions } from 'features/embeddedSidebar/hooks/useDocumentChatSessions'
import type { RestoredDocChatTurnSettlement } from 'features/embeddedSidebar/hooks/useRestoredDocChatTurnSettlement'
import {
  excludeDeletedHistorySessions,
  mergeLiveAndPersistedSessions,
  splitActiveAndArchivedSessions,
} from 'features/embeddedSidebar/sessions/agenticChatSessionList'
import type { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import { useOptionalWorkspaceTabs } from 'features/workspaceTabs/contexts/WorkspaceTabsContext'
import { useCallback, useMemo } from 'react'

import { AgenticChatHistorySidebar } from './AgenticChatHistorySidebar'

interface EmbeddedSidebarChatHistoryProps {
  openTabs: DocumentTab[] | undefined
  showHistory: boolean
  siteId: string | undefined
}

export function EmbeddedSidebarChatHistory({
  openTabs,
  showHistory,
  siteId,
}: EmbeddedSidebarChatHistoryProps) {
  const {
    deleteSession: deleteAgenticChatSession,
    pinSession: pinAgenticChatSession,
    renameSession: renameAgenticChatSession,
    sessions: liveAgenticChatSessions,
  } = useAgenticChatSessionHistory(openTabs, siteId)
  const workspaceTabs = useOptionalWorkspaceTabs()
  const onRestoredTurnSettled = useCallback(
    ({ conversation, sessionKey, tabId }: RestoredDocChatTurnSettlement) => {
      workspaceTabs?.setOpenTabs((currentTabs) =>
        applyDocumentChatConversationToOpenTabs({
          conversation,
          openTabs: currentTabs,
          sessionKey,
          tabId,
        })
      )
    },
    [workspaceTabs]
  )
  const {
    activeLoadFailed,
    archiveSession: persistArchiveSession,
    archiveSessions,
    deleteSession: persistDeleteSession,
    deleteSessions,
    deletedSessionKeys,
    archivedHasMore,
    hasArchivedSessions,
    hasExpandedArchived,
    hydrateSession,
    isLoadingActive,
    isLoadingArchived,
    loadMoreArchived,
    persistedSessions,
    pinPersistedSession,
    renamePersistedSession,
    retryActiveLoad,
    retrySession,
    toggleArchived,
    unarchiveSession: persistUnarchiveSession,
    unarchiveSessions,
  } = useDocumentChatSessions({
    liveSessions: liveAgenticChatSessions,
    showHistory,
    onRestoredTurnSettled,
    siteId,
  })
  const { activeSessions: agenticChatSessions, archivedSessions } = useMemo(
    () =>
      splitActiveAndArchivedSessions(
        excludeDeletedHistorySessions(
          mergeLiveAndPersistedSessions({
            liveSessions: liveAgenticChatSessions,
            persistedSessions,
          }),
          deletedSessionKeys
        )
      ),
    [deletedSessionKeys, liveAgenticChatSessions, persistedSessions]
  )
  const renameAgenticSession = useCallback(
    (sessionKey: string, title: string) => {
      renameAgenticChatSession(sessionKey, title)
      void renamePersistedSession(sessionKey, title)
    },
    [renameAgenticChatSession, renamePersistedSession]
  )
  const pinAgenticSession = useCallback(
    (sessionKey: string, pinned: boolean) => {
      pinAgenticChatSession(sessionKey, pinned)
      void pinPersistedSession(sessionKey, pinned).then((persisted) => {
        if (!persisted) pinAgenticChatSession(sessionKey, !pinned)
      })
    },
    [pinAgenticChatSession, pinPersistedSession]
  )
  const handleRetryActiveLoad = useCallback(() => {
    void retryActiveLoad()
  }, [retryActiveLoad])
  const handleRetrySession = useCallback(
    (session: RecordedAgenticChatSession) => {
      void retrySession(session)
    },
    [retrySession]
  )

  if (!showHistory) return null

  return (
    <AgenticChatHistorySidebar
      archivedHasMore={archivedHasMore}
      archivedSessions={archivedSessions}
      hasArchivedSessions={hasArchivedSessions}
      hasExpandedArchived={hasExpandedArchived}
      hydrateSession={hydrateSession}
      isLoadingActive={isLoadingActive}
      isLoadingArchived={isLoadingArchived}
      loadFailed={activeLoadFailed}
      onArchiveSession={persistArchiveSession}
      onArchiveSessions={archiveSessions}
      onDeleteSession={persistDeleteSession}
      onDeleteSessions={deleteSessions}
      onLoadMoreArchived={loadMoreArchived}
      onPinSession={pinAgenticSession}
      onRemoveLocalSession={deleteAgenticChatSession}
      onRenameSession={renameAgenticSession}
      onRetryActiveLoad={handleRetryActiveLoad}
      onToggleArchived={toggleArchived}
      onUnarchiveSession={persistUnarchiveSession}
      onUnarchiveSessions={unarchiveSessions}
      onRetrySession={handleRetrySession}
      sessions={agenticChatSessions}
      siteId={siteId}
    />
  )
}
